import { Link, Outlet, useLocation } from "@tanstack/react-router";
import {
	Activity,
	Building2,
	ChartColumn,
	CircleUserRound,
	CreditCard,
	Home,
	KeyRound,
	LogOut,
	type LucideIcon,
	MessageSquare,
	PanelLeft,
	PanelLeftClose,
	Plug,
	Settings,
	Users,
	Brain,
	Webhook,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Button } from "@lawline/ui/components/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@lawline/ui/components/dropdown-menu";
import logoIcon from "@lawline/ui/assets/logo-icon.svg";
import { Tooltip, TooltipContent, TooltipTrigger } from "@lawline/ui/components/tooltip";

import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "use-intl";

import { AuthProvider, useAuth } from "@platform/auth/context";
import type { PermissionRequest } from "@platform/auth/guards";
import { authClient } from "@platform/auth/client";
import {
	sessionMutationOptions,
	useSessionTransition,
} from "@platform/auth/session-transition.context";
import { billingQueryOptions } from "@platform/features/billing/billing.queries";
import { DocsLink } from "./docs-link";
import { FeedbackDialog } from "./feedback-dialog";
import { BillingNotSetUpNotice } from "./billing-notice";
import { TenantSwitcher } from "./tenant-switcher";
import { SiteSearch } from "./search";
import { SuspendedNotice } from "./suspended";

type NavItem = {
	to:
		| "/"
		| "/api-keys"
		| "/ai-apps"
		| "/webhooks"
		| "/usage"
		| "/tenant"
		| "/memory"
		| "/billing"
		| "/settings"
		| "/admin/telemetry"
		| "/admin/users"
		| "/admin/tenants"
		| "/admin/communication";
	// Key under common.nav.items used to translate the label at render.
	labelKey:
		| "home"
		| "tenant"
		| "memory"
		| "apiKeys"
		| "aiApps"
		| "webhooks"
		| "usage"
		| "billing"
		| "adminTelemetry"
		| "adminUsers"
		| "adminTenants"
		| "adminCommunication";
	icon: LucideIcon;
	exact?: boolean;
	permission?: PermissionRequest;
};

type NavSection = {
	// Key under common.nav.sections used to translate the title at render.
	titleKey: "general" | "platform" | "admin";
	items: readonly NavItem[];
	// Shown only to cross-tenant Lawline staff (user.role === "admin").
	adminOnly?: boolean;
};

const NAV: readonly NavSection[] = [
	{
		titleKey: "general",
		items: [{ to: "/", labelKey: "home", icon: Home, exact: true }],
	},
	{
		titleKey: "platform",
		items: [
			{ to: "/tenant", labelKey: "tenant", icon: Building2 },
			{
				to: "/memory",
				labelKey: "memory",
				icon: Brain,
				permission: { memory: ["read"] },
			},
			{
				to: "/api-keys",
				labelKey: "apiKeys",
				icon: KeyRound,
				permission: { apiKey: ["read"] },
			},
			{ to: "/ai-apps", labelKey: "aiApps", icon: Plug },
			{
				to: "/webhooks",
				labelKey: "webhooks",
				icon: Webhook,
				permission: { webhook: ["read"] },
			},
			{
				to: "/usage",
				labelKey: "usage",
				icon: ChartColumn,
				permission: { usage: ["read"] },
			},
			{
				to: "/billing",
				labelKey: "billing",
				icon: CreditCard,
				permission: { billing: ["manage"] },
			},
		],
	},
	{
		titleKey: "admin",
		adminOnly: true,
		items: [
			{ to: "/admin/telemetry", labelKey: "adminTelemetry", icon: Activity },
			{ to: "/admin/users", labelKey: "adminUsers", icon: Users },
			{
				to: "/admin/tenants",
				labelKey: "adminTenants",
				icon: Building2,
			},
			{ to: "/admin/communication", labelKey: "adminCommunication", icon: MessageSquare },
		],
	},
];

// Label slides/fades in only while the sidebar is expanded (group-hover). It is
// kept mounted but clipped by the sidebar's overflow-hidden when collapsed.
const LABEL =
	"whitespace-nowrap text-xs opacity-0 transition-opacity duration-300 ease-[cubic-bezier(0.65,0,0.35,1)] group-hover/peek:opacity-100 group-has-[[data-state=open]]:opacity-100";

// The section header is clipped to h-0 when collapsed and grows in on expand.
// The dividing border does the inverse. Collapsed shows rails, expanded shows
// labeled groups.
const SECTION_HEADER =
	"h-0 overflow-hidden whitespace-nowrap px-3 text-[0.625rem] font-semibold uppercase tracking-wider text-muted-foreground opacity-0 transition-all duration-300 ease-[cubic-bezier(0.65,0,0.35,1)] group-hover/peek:mb-1 group-hover/peek:h-4 group-hover/peek:opacity-100 group-has-[[data-state=open]]:mb-1 group-has-[[data-state=open]]:h-4 group-has-[[data-state=open]]:opacity-100";

// Pages scoped to the active tenant. A suspended member is locked out of these
// (server 403s every /platform/* read), so the shell shows the suspended notice
// in their place. Account-level pages (/, /settings) stay reachable.
const TENANT_SCOPED_PREFIXES = [
	"/tenant",
	"/memory",
	"/api-keys",
	"/ai-apps",
	"/webhooks",
	"/usage",
	"/billing",
];

function isTenantScoped(pathname: string): boolean {
	return TENANT_SCOPED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

// Authenticated app shell. AuthProvider mounts here (inside the session-guarded
// route) so its session/member reads never fire on public routes like /login.
export function AppShell() {
	return (
		<AuthProvider>
			<Shell />
		</AuthProvider>
	);
}

function Shell() {
	const { can, user, suspended } = useAuth();
	const tNav = useTranslations("common.nav");
	const tAccount = useTranslations("common.account");
	const [pinned, setPinned] = useState(() => localStorage.getItem("sidebar-pinned") === "true");

	const togglePinned = useCallback(() => {
		setPinned((v) => {
			const next = !v;
			localStorage.setItem("sidebar-pinned", String(next));
			return next;
		});
	}, []);

	// Cmd/Ctrl+B toggles the pinned sidebar, same as the rail button.
	useEffect(() => {
		function onKey(e: KeyboardEvent) {
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "b") {
				e.preventDefault();
				togglePinned();
			}
		}
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [togglePinned]);
	const { pathname } = useLocation();
	const { data: tenant } = authClient.useActiveOrganization();
	const { data: billing } = useQuery({
		...billingQueryOptions(),
		enabled: !suspended && !!tenant?.id,
	});
	const blocked = suspended && isTenantScoped(pathname);
	const billingNotSetUp =
		!suspended && billing?.billingStatus === "pending" && isTenantScoped(pathname);

	const changeSession = useSessionTransition();

	async function signOut() {
		await changeSession(async () => {
			const { data, error } = await authClient.signOut({ fetchOptions: sessionMutationOptions });
			if (error || !data?.success) throw new Error("Sign out is unconfirmed");
			return "reload";
		}, "/login");
	}

	return (
		<div className="relative flex h-screen">
			{/* Outer <aside> reserves FLOW width only: rail width normally, full
			    width when pinned, so pinning displaces the content column. Hover
			    leaves flow width at the rail, so hover-expand overlays instead. */}
			<aside className="group relative w-14 shrink-0 transition-[width] duration-300 ease-[cubic-bezier(0.65,0,0.35,1)] select-none [--w-sidebar:11rem] has-[[data-state=open]]:w-[var(--w-sidebar)]">
				{/* Visual panel, absolutely positioned over the flow. Expands on hover
				    (floats over content, no displace) AND when pinned (aligns with the
				    widened flow). z-40 keeps it above the header/content. */}
				<div
					data-slot="app-sidebar"
					className="bg-background absolute inset-y-0 left-0 z-40 flex w-14 flex-col overflow-hidden border-r transition-[width] duration-300 ease-[cubic-bezier(0.65,0,0.35,1)] has-[.peek:hover]:w-[var(--w-sidebar)] has-[[data-state=open]]:w-[var(--w-sidebar)]"
				>
					{/* When pinned, this hidden marker makes every existing
				    group-has-[[data-state=open]] selector fire (width, labels,
				    section headers), so the sidebar stays expanded persistently. */}
					{pinned && <span data-state="open" className="hidden" />}
					{/* Only hovering this .peek zone (header + nav) expands the sidebar.
				    The toggle compartment below sits outside it, so hovering the
				    collapse/expand button never auto-opens the rail. */}
					<div className="peek group/peek flex min-h-0 flex-1 flex-col">
						<div className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
							<Link to="/" className="shrink-0">
								<img src={logoIcon} alt="" className="size-6 dark:invert" />
							</Link>
							<TenantSwitcher />
						</div>
						<nav className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-y-contain p-2">
							{NAV.map((section) => {
								if (section.adminOnly && user?.role !== "admin") return null;
								const items = section.items.filter(
									(item) =>
										(!item.permission || can(item.permission)) &&
										!(suspended && isTenantScoped(item.to)),
								);
								if (items.length === 0) return null;
								return (
									<div
										key={section.titleKey}
										className={
											"flex flex-col gap-0.5 py-2 first:pt-0 [&:not(:first-child)]:border-t group-hover/peek:[&:not(:first-child)]:border-t-0 group-has-[[data-state=open]]:[&:not(:first-child)]:border-t-0"
										}
									>
										<div className={SECTION_HEADER}>{tNav(`sections.${section.titleKey}`)}</div>
										{items.map((item) => {
											const Icon = item.icon;
											return (
												<Button
													key={item.to}
													asChild
													variant="ghost"
													size="sm"
													className="h-9 justify-start gap-3 text-xs"
												>
													<Link
														to={item.to}
														activeOptions={{ exact: item.exact ?? false }}
														className="[&.active]:bg-accent [&.active_svg]:text-foreground [&.active]:font-medium"
													>
														<Icon className="text-muted-foreground size-4 shrink-0" />
														<span className={LABEL}>{tNav(`items.${item.labelKey}`)}</span>
													</Link>
												</Button>
											);
										})}
									</div>
								);
							})}
						</nav>

						{/* Documentation and feedback sit at the bottom of the hover zone so
						    their labels expand with the sidebar, like nav items. */}
						<div className="flex flex-col gap-0.5 border-t p-2">
							<DocsLink labelClassName={LABEL} />
							<FeedbackDialog labelClassName={LABEL} />
						</div>
					</div>

					<div className="border-t p-2">
						<Tooltip>
							<TooltipTrigger asChild>
								<Button variant="ghost" size="icon" onClick={togglePinned} className="size-9">
									{pinned ? (
										<PanelLeftClose className="text-muted-foreground size-4 shrink-0" />
									) : (
										<PanelLeft className="text-muted-foreground size-4 shrink-0" />
									)}
								</Button>
							</TooltipTrigger>
							<TooltipContent>{tNav("sidebarTooltip")}</TooltipContent>
						</Tooltip>
					</div>
				</div>
			</aside>

			<div className="flex min-h-0 min-w-0 flex-1 flex-col">
				<header className="bg-background relative z-30 flex h-12 shrink-0 items-center border-b px-4">
					{/* The search bar queries /platform, so a suspended member never sees it. */}
					{!suspended && (
						<div className="absolute top-1/2 left-1/2 w-full max-w-xs -translate-x-1/2 -translate-y-1/2 px-4">
							<SiteSearch />
						</div>
					)}

					<DropdownMenu>
						<Tooltip>
							<TooltipTrigger asChild>
								<DropdownMenuTrigger asChild>
									<Button variant="ghost" size="icon" className="ml-auto">
										<CircleUserRound className="text-muted-foreground size-5" />
									</Button>
								</DropdownMenuTrigger>
							</TooltipTrigger>
							<TooltipContent>{tAccount("menu")}</TooltipContent>
						</Tooltip>
						<DropdownMenuContent align="end" className="w-56">
							{user?.name ? (
								<>
									<DropdownMenuLabel>{user.name}</DropdownMenuLabel>
									<DropdownMenuSeparator />
								</>
							) : null}
							<DropdownMenuItem asChild>
								<Link to="/settings">
									<Settings className="size-4" />
									{tAccount("settings")}
								</Link>
							</DropdownMenuItem>
							<DropdownMenuItem
								onClick={signOut}
								className="focus:text-destructive focus:[&_svg]:text-destructive!"
							>
								<LogOut className="size-4" />
								{tAccount("logOut")}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</header>

				{/* A notice replaces the outlet on tenant-scoped pages when the active
				    membership is suspended, because the server 403s those reads. */}
				<main className="flex-1 overflow-auto overscroll-contain p-4 sm:px-20">
					{blocked ? (
						<SuspendedNotice />
					) : billingNotSetUp && pathname !== "/billing" ? (
						<BillingNotSetUpNotice />
					) : (
						<Outlet />
					)}
				</main>
			</div>
		</div>
	);
}
